import { PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import type { RequireItemRow, RequireRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Descriptions, Drawer, Segmented, Space, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../api/client';
import { MoneyText } from '../components/Money';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';
import { printDocument } from '../print';

const PAGE_SIZE = 20;

const STATUS_META: Record<string, { text: string; color: string }> = {
  DRAFT: { text: '草稿', color: 'default' },
  SUBMITTED: { text: '已提交', color: 'processing' },
  APPROVED: { text: '已批准', color: 'blue' },
  ORDERED: { text: '已转采购', color: 'green' },
  REJECTED: { text: '已驳回', color: 'red' },
  CLOSED: { text: '已关闭', color: 'default' }
};

/**
 * 请购单只读跟踪（docs/01 §6.2-5）：
 * 与运营端 `PurchaseOps` 共用 `/api/wl/require`，医院端换视角只读 + 打印。
 */
export default function RequireList() {
  const [status, setStatus] = useState<string>('ALL');
  const [currentId, setCurrentId] = useState<string | null>(null);

  const query = useInfiniteQuery({
    queryKey: ['require', 'list', status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.procurement.requires.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        requireStatus: status === 'ALL' ? undefined : status
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const detail = useQuery({
    queryKey: ['require', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.procurement.requires.detail(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<RequireRow> = [
    {
      title: '请购单号',
      dataIndex: 'bizNo',
      width: 210,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrentId(row.id)}>{value}</Typography.Link>
    },
    { title: '请购期间', dataIndex: 'requirePeriod', width: 110, render: (value: string | null) => value || '-' },
    { title: '流程类型', dataIndex: 'flowType', width: 110, render: (value: string | null) => value || '-' },
    {
      title: '状态',
      dataIndex: 'requireStatus',
      width: 110,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '数量', dataIndex: 'totalQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '金额', dataIndex: 'totalAmount', width: 130, align: 'right', render: (value: number) => <MoneyText value={value} /> },
    {
      title: '转采购单号',
      dataIndex: 'convertedOrderNos',
      width: 220,
      ellipsis: { showTitle: false },
      render: ellipsisCell('convertedOrderNos')
    },
    { title: '申请时间', dataIndex: 'bizDate', width: 150, render: (value: string) => formatDisplayTime(value) }
  ];

  const itemColumns: ColumnsType<RequireItemRow> = [
    { title: '行', dataIndex: 'lineNo', width: 56 },
    { title: '物资编码', dataIndex: 'materialCode', width: 150, render: (value: string | null) => value || '-' },
    { title: '物资名称', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '规格', dataIndex: 'spec', width: 140, render: (value: string | null) => value || '-' },
    { title: '单位', dataIndex: 'unitName', width: 80, render: (value: string | null) => value || '-' },
    { title: '请购量', dataIndex: 'reqQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '批准量', dataIndex: 'approvedQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '单价', dataIndex: 'unitPrice', width: 110, align: 'right', render: (value: number) => formatAmount(value, 4) },
    { title: '金额', dataIndex: 'amount', width: 120, align: 'right', render: (value: number) => formatAmount(value) }
  ];

  const print = () => {
    const doc = detail.data;
    if (!doc) return;
    printDocument({
      title: '请购单',
      subtitle: doc.require.bizNo,
      meta: [
        ['请购期间', doc.require.requirePeriod || '-'],
        ['流程类型', doc.require.flowType || '-'],
        ['状态', STATUS_META[doc.require.requireStatus]?.text ?? doc.require.requireStatus],
        ['申请时间', formatDisplayTime(doc.require.bizDate)],
        ['数量合计', formatQty(doc.require.totalQty)],
        ['金额合计', formatAmount(doc.require.totalAmount)]
      ],
      columns: [
        { title: '行' },
        { title: '物资编码' },
        { title: '物资名称' },
        { title: '规格' },
        { title: '单位' },
        { title: '请购量', align: 'right' },
        { title: '批准量', align: 'right' },
        { title: '单价', align: 'right' },
        { title: '金额', align: 'right' }
      ],
      rows: doc.items.map((item) => [
        item.lineNo,
        item.materialCode ?? '',
        item.materialName ?? '',
        item.spec ?? '',
        item.unitName ?? '',
        formatQty(item.reqQty),
        formatQty(item.approvedQty),
        formatAmount(item.unitPrice, 4),
        formatAmount(item.amount)
      ]),
      footer: `备注：${doc.require.remark || '-'}`
    });
  };

  return (
    <Card
      className="page-card"
      title="请购单"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
            刷新
          </Button>
          <Button icon={<PrinterOutlined />} disabled={!detail.data} onClick={print}>
            打印
          </Button>
        </Space>
      }
    >
      <Segmented
        value={status}
        onChange={(value) => setStatus(String(value))}
        options={[
          { label: '全部', value: 'ALL' },
          { label: '已提交', value: 'SUBMITTED' },
          { label: '已批准', value: 'APPROVED' },
          { label: '已转采购', value: 'ORDERED' }
        ]}
        style={{ marginBottom: 12 }}
      />

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="请购单加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}

      <Table<RequireRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无请购单' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrentId(row.id) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Drawer
        title={detail.data?.require.bizNo ?? '请购单详情'}
        open={currentId !== null}
        onClose={() => setCurrentId(null)}
        width={900}
        extra={
          <Button icon={<PrinterOutlined />} onClick={print} disabled={!detail.data}>
            打印
          </Button>
        }
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : detail.data ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="状态">
                {STATUS_META[detail.data.require.requireStatus]?.text ?? detail.data.require.requireStatus}
              </Descriptions.Item>
              <Descriptions.Item label="请购期间">{detail.data.require.requirePeriod || '-'}</Descriptions.Item>
              <Descriptions.Item label="流程类型">{detail.data.require.flowType || '-'}</Descriptions.Item>
              <Descriptions.Item label="转采购单号">{detail.data.require.convertedOrderNos || '-'}</Descriptions.Item>
              <Descriptions.Item label="数量合计">{formatQty(detail.data.require.totalQty)}</Descriptions.Item>
              <Descriptions.Item label="金额合计">{formatAmount(detail.data.require.totalAmount)}</Descriptions.Item>
              <Descriptions.Item label="申请时间">{formatDisplayTime(detail.data.require.bizDate)}</Descriptions.Item>
              <Descriptions.Item label="备注">{detail.data.require.remark || '-'}</Descriptions.Item>
            </Descriptions>
            <Table<RequireItemRow>
              rowKey="id"
              size="small"
              columns={itemColumns}
              dataSource={detail.data.items}
              pagination={false}
              scroll={{ x: sumWidth(itemColumns) }}
            />
          </>
        ) : null}
      </Drawer>
    </Card>
  );
}
